import React from 'react';
import { CountryTemplate } from '../../components/CountryTemplate';

export const QatarPage = () => {
  const qatarData = {
    name: 'Qatar',
    region: 'GCC',
    color: '#A060B0',
    metaTitle: 'Pharma & Nutraceutical Registration in Qatar | MoPH | CTD/eCTD Dossiers',
    metaDescription: 'Find how to register pharmaceutical and nutraceutical products in Qatar. MoPH requirements, CTD/eCTD dossier expectations, dietary-supplement rules, required documents.',
    
    hero: {
      title: 'Pharma & Nutraceutical Registration in Qatar',
      subtitle: 'A high-income Gulf state with advanced healthcare infrastructure, strong import-dependence and clear registration pathways through MoPH - making it a key GCC export market.',
      cta: 'Start Your Qatar Registration'
    },
    
    atAGlance: [
      { label: 'Population', value: '~2.9 million' },
      { label: 'Income Level', value: 'High-income economy' },
      { label: 'Health Expenditure', value: '~4-5% of GDP' },
      { label: 'Pharma Import Share', value: 'Very high (80%+)' },
      { label: 'Market Type', value: 'Premium, high-value' },
      { label: 'Supplement Timeline', value: '5-9 weeks' },
      { label: 'Pharma Timeline', value: 'Several months' }
    ],
    
    marketOverview: {
      title: 'Market Overview: Premium Market + Import Reliance + Regulatory Maturity',
      stats: [
        'Pharmacy & Drug Control Department of MoPH regulates pharmaceutical and herbal medicines',
        'Most pharmaceuticals (80%+) are imported',
        'Dossiers must follow ICH-CTD/eCTD standards',
        'Dietary supplements, herbal products and medicated cosmetics regulated under MoPH',
        'Clear legal basis: Law No. 1/1986 on Registration of Pharmaceutical Companies & Products',
        'Affluent wellness-oriented consumers and large expatriate population'
      ],
      opportunities: [
        'High per-capita healthcare spending',
        'Premium market positioning for quality products',
        'Affluent and wellness-oriented consumer base',
        'Strategic GCC hub for regional expansion'
      ]
    },
    
    diseaseburden: {
      title: 'Disease Profile: Lifestyle Diseases + High-End Healthcare Demand',
      subtitle: 'Qatar health profile mirrors other GCC states with significant NCD burden.',
      categories: [
        {
          name: 'Non-Communicable Diseases',
          items: [
            'High prevalence of overweight/obesity',
            'Elevated diabetes rates',
            'Significant cardiovascular disease burden',
            'Rising NCD prevalence across population',
            'Pharma demand: Antidiabetics, antihypertensives, lipid-lowering agents',
            'Pharma demand: Cardiovascular, renal and specialty medicines',
            'Premium market for branded and high-end generics'
          ]
        },
        {
          name: 'Wellness & Lifestyle',
          items: [
            'Affluent consumer base',
            'Large expatriate population',
            'Strong wellness culture',
            'Health-supplement demand driven by wellness-oriented consumers',
            'Nutra opportunities: Metabolic support, weight-management, joint health',
            'Nutra opportunities: Vitamins/minerals for high-income segments',
            'Compliance with supplement registration and labelling critical'
          ]
        }
      ]
    },
    
    regulatoryFramework: {
      authority: 'Ministry of Public Health (MoPH) - Pharmacy & Drug Control Department',
      description: 'MoPH handles pharmaceutical registrations including pharmaceutical, herbal and dietary supplement products. Legal basis: Law No. 1/1986 on Registration of Pharmaceutical Companies and Products. Registration & Drug Pricing Section oversees pharmaceutical and herbal medicines. ICH-CTD format adopted with increasing eCTD implementation.',
      pharma: {
        timeline: 'Pharmaceuticals: several months depending on complexity and queries. CTD review includes pricing, lab verification and committee decision. Variations, renewals and lifecycle management maintained post-approval.',
        requirements: [
          'Foreign pharmaceuticals must register via local authorised entity/agent licensed for distribution',
          'Submission to MoPH in CTD format with updated path to eCTD',
          'GMP certificate (authenticated)',
          'Free Sale Certificate (FSC) of product in country of origin',
          'Certificate of analysis, composition, manufacturing licence',
          'Six (6) samples of product (for evaluation)',
          'Label/leaflet in Arabic and/or English',
          'Authentication via Ministry of Foreign Affairs of Qatar for certain documents',
          'Price approval and distribution licence follow after registration'
        ],
        insight: 'Qatar follows CTD format moving toward eCTD. Authentication of documents via Qatar MFA required for certain items. Typical pharma timeline several months with price approval process. Premium market allows quality product positioning.'
      },
      nutra: {
        timeline: 'Supplements & nutraceuticals: ~5-9 weeks assuming complete dossier. Faster than pharmaceutical route if correctly classified.',
        requirements: [
          'Registration Requirements for herbal products, dietary supplements and medicated cosmetics',
          'Free Sale Certificate (FSC), composition certificate, COA, samples required',
          'Label compliance (Arabic/English) mandatory',
          'Sport supplements must carry doping warning in English/Arabic',
          'Local agent/importer must apply and file required documentation',
          'Halal/pork-free/alcohol-free certificates where needed',
          'Clear claims required: avoid disease-treatment claims or high doses',
          'High doses or medicinal claims trigger pharmaceutical classification'
        ],
        insight: 'Supplement timeline 5-9 weeks with complete documentation. Sport supplements require specific doping risk warning. Classification critical - disease-treatment claims or high nutrient levels push to pharmaceutical route. Must ensure local importer in place with all certifications.'
      }
    },
    
    pharmaPathway: {
      title: 'Pharmaceutical Registration in Qatar',
      steps: [
        'Local Agent / Applicant - Foreign pharmaceuticals must register via local authorised entity/agent',
        'Agent must be licensed for distribution in Qatar',
        'Dossier Format - CTD / eCTD - Submission to MoPH in CTD format',
        'Updated path to eCTD following GCC harmonisation',
        'GMP certificate from country of origin (authenticated)',
        'Free Sale Certificate (FSC) of product in country of origin',
        'Certificate of analysis, composition details',
        'Manufacturing licence',
        'Six (6) samples of product for evaluation',
        'Label/leaflet in Arabic and/or English',
        'Authentication via Ministry of Foreign Affairs of Qatar for certain documents',
        'Assessment: CTD review with pricing, lab verification, committee decision',
        'Timeline: Several months depending on dossier completeness',
        'Price approval and distribution licence follow registration',
        'Variations, renewals and lifecycle management post-approval'
      ]
    },
    
    nutraPathway: {
      title: 'Nutraceuticals & Dietary Supplements in Qatar',
      steps: [
        'Regulatory Channel - Registration Requirements document for herbal/supplements/medicated cosmetics',
        'Free Sale Certificate (FSC), composition certificate, COA, samples required',
        'Label compliance (Arabic/English) mandatory',
        'Sport supplements warning: Licensing does not guarantee product free from banned substances',
        'Athlete uses dietary supplements at own risk of testing positive for doping',
        'Local agent/importer must apply and file required documentation',
        'Ensure label compliance (Arabic/English)',
        'Absence of banned substances verification',
        'Halal/pork-free/alcohol-free certificates where needed',
        'Clear claims: avoid disease-treatment or high nutrient doses',
        'Classification determines pathway - medicinal territory triggers pharma route',
        'Timeline: Typically 5-9 weeks with complete dossier',
        'Faster process than pharmaceutical registration'
      ]
    },
    
    requiredDocs: {
      'Pharmaceuticals (MoPH)': [
        'Application form via MoPH online services',
        'CTD/eCTD dossier (Modules 1-5)',
        'Manufacturing site licences',
        'GMP certificate',
        'CPP/FSC',
        'Stability data',
        'Local agent/MAH licence and agent agreement',
        'Packaging/label artwork (Arabic & English)'
      ],
      'Nutraceuticals / Supplements': [
        'Free Sale Certificate (country of origin, authenticated by Qatar MFA)',
        'GMP certificate of manufacturer (authenticated if needed)',
        'Composition certificate (active & inactive ingredients)',
        'Certificate of analysis (finished product)',
        'Label & leaflet in Arabic/English with required disclaimers',
        'Samples (six units) for analysis'
      ]
    },
    
    whyThisCountry: {
      title: 'Why Qatar Is Key for Your Gulf Strategy',
      reasons: [
        'High-income market with premium healthcare infrastructure',
        'Strong dependency on imported pharmaceutical products (80%+)',
        'Regulatory maturity with clear legal basis (Law No.1/1986)',
        'CTD/eCTD alignment and digital submission pathways',
        'Well-defined supplement regulation with transparent documentation',
        'Success in Qatar supports broader GCC penetration',
        'Affluent population with wellness focus'
      ],
      bestPortfolio: [
        'Pharma: Premium generics for NCDs (diabetes, cardiovascular), specialty therapies, branded imports with quality documentation',
        'Nutra: High-end supplements (cardio/metabolic support, weight-management, premium vitamins/minerals) for affluent and expatriate populations with compliance to regulatory and label demands'
      ]
    },
    
    howWeHelp: {
      title: 'How TheApostrophe Helps You Enter Qatar',
      services: [
        'Market & Portfolio Fit - Map products to Qatar high-income patient base, NCD focus and wellness trends',
        'CTD/eCTD Dossier Strategy - Adapt/build dossiers aligned with MoPH requirements, full GMP/CPP support',
        'Arabic/English Labels - Ensure bilingual labelling meets Qatar specifications',
        'Supplement Strategy - Position nutraceuticals under herbal/supplement route correctly, avoid mis-classification',
        'Agent & Partner Coordination - Support Qatari local agent/MAH selection, manage portal submissions',
        'Repeatable GCC Strategy - Build reusable assets for broader GCC launch (Saudi, UAE, Kuwait, Bahrain, Oman)'
      ]
    },
    
    faqs: [
      {
        question: 'Who regulates medicines in Qatar?',
        answer: 'The Ministry of Public Health (MoPH) via its Pharmacy & Drug Control Department handles registration of pharmaceutical, herbal and dietary-supplement products.'
      },
      {
        question: 'What dossier format does Qatar require?',
        answer: 'CTD format is required; Qatar is moving toward eCTD submissions, consistent with GCC harmonisation and international standards.'
      },
      {
        question: 'Are free-sale certificates required for supplements?',
        answer: 'Yes. The registration requirements list a Free Sale Certificate (authenticated) as mandatory for herbal/supplements registration in Qatar.'
      },
      {
        question: 'How long does registration take?',
        answer: 'Supplements/nutraceuticals: typically 5-9 weeks with complete dossier. Pharmaceuticals: several months, depending on complexity, queries and pricing review.'
      },
      {
        question: 'Does Qatar accept approvals from other countries?',
        answer: 'Approvals from other jurisdictions may support the application but do not replace full dossier submission. MoPH requires independent evaluation of all products.'
      }
    ]
  };

  return <CountryTemplate countryData={qatarData} />;
};
